"use client";

import { useCallback, useEffect, useState } from "react";
import { signIn } from "../client";
import { getSafeAuthCallbackUrl } from "./callback-url";

const CANONICAL_CODEX_TEST_EMAIL = "mike@swansonking.com";

/**
 * Sign-in-only form rendered alongside the Google button on the sign-in page.
 * Visible only when the page is being served from a local hostname
 * (`localhost`, `127.0.0.1`, `::1`, or any `*.localhost` subdomain) so the
 * form never appears in deployed environments. It intentionally exposes one
 * canonical Codex identity and has no account-creation path.
 *
 * The matching server endpoint is also gated — `emailAndPassword` is only
 * enabled in `packages/auth/server.ts` when `NODE_ENV !== "production"`.
 * Either gate alone is sufficient; both together are belt and suspenders.
 */
export function DevCredentialsForm() {
  const [isLocal, setIsLocal] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const host = window.location.hostname;
    setIsLocal(
      host === "localhost" ||
        host === "127.0.0.1" ||
        host === "::1" ||
        host.endsWith(".localhost"),
    );
  }, []);

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      setLoading(true);
      try {
        const callbackURL = getSafeAuthCallbackUrl();
        const result = await signIn.email({
          email: CANONICAL_CODEX_TEST_EMAIL,
          password,
          callbackURL,
        });
        if (result.error) {
          throw new Error(result.error.message ?? "Sign in failed");
        }
        const hasOAuthContinuation = new URLSearchParams(window.location.search).has("sig");
        const redirect = result.data as { redirect?: boolean; url?: string } | undefined;
        if (redirect?.redirect && redirect.url) {
          window.location.assign(redirect.url);
        } else if (!hasOAuthContinuation) {
          window.location.href = callbackURL;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Authentication failed");
      } finally {
        setLoading(false);
      }
    },
    [password],
  );

  if (!isLocal) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center gap-3 text-muted-foreground text-xs uppercase tracking-wider">
        <span className="h-px flex-1 bg-border" />
        Dev only
        <span className="h-px flex-1 bg-border" />
      </div>
      <p className="mb-1 text-muted-foreground text-sm">
        Use the canonical Codex test account.
      </p>
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1 text-left">
          <span className="font-medium text-foreground text-sm">
            Email <span className="font-normal text-muted-foreground">({CANONICAL_CODEX_TEST_EMAIL})</span>
          </span>
          <input
            autoComplete="email"
            className="h-10 rounded-md border border-border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            name="email"
            readOnly
            spellCheck={false}
            type="email"
            defaultValue="mike@swansonking.com"
          />
        </label>

        <label className="flex flex-col gap-1 text-left">
          <span className="font-medium text-foreground text-sm">Password</span>
          <input
            autoComplete="current-password"
            className="h-10 rounded-md border border-border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            minLength={8}
            onChange={handlePasswordChange}
            name="password"
            placeholder="••••••••"
            required
            type="password"
            value={password}
          />
        </label>

        {error ? (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        ) : null}

        <button
          className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 font-medium text-primary-foreground text-sm shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
          disabled={loading}
          type="submit"
        >
          {loading ? "Working…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
